import { differenceInCalendarDays, format, startOfDay } from 'date-fns';
import clsx from 'clsx';

import { eventStartDay } from '../event-day';
import { retintEventBackground, useResolvedTheme } from '../theme';
import type { CalendarEvent } from '../types';
import { useNow } from '../useNow';

interface Props {
  events: CalendarEvent[];
  onPick: (e: CalendarEvent) => void;
  onRemove: (id: string) => void;
}

// How long until an event starts, in the shortest form that stays honest.
// Days for anything past today; hours or minutes once it's today and timed;
// "now" while it's running.
function remaining(e: CalendarEvent, now: Date): { text: string; near: boolean } {
  const day = eventStartDay(e);
  if (!day) return { text: '', near: false };
  const days = differenceInCalendarDays(day, startOfDay(now));
  if (days > 0) return { text: `${days}d`, near: days === 1 };
  if (days < 0) return { text: 'now', near: true };
  if (e.all_day) return { text: 'today', near: true };

  const start = e.start_time ? new Date(e.start_time) : null;
  const ms = start ? start.getTime() - now.getTime() : 0;
  if (ms <= 0) return { text: 'now', near: true };
  const minutes = Math.round(ms / 60_000);
  if (minutes < 60) return { text: `${minutes}m`, near: true };
  return { text: `${Math.round(minutes / 60)}h`, near: true };
}

function when(e: CalendarEvent): string {
  const day = eventStartDay(e);
  if (!day) return '';
  if (e.all_day) return format(day, 'EEE d MMM yyyy');
  return format(new Date(e.start_time as string), 'EEE d MMM yyyy, h:mm a');
}

export default function CountdownBadges({ events, onPick, onRemove }: Props) {
  const theme = useResolvedTheme();
  const now = useNow();
  if (events.length === 0) return null;

  return (
    <div className="border-t border-[var(--border)] px-2 py-2">
      <div className="max-h-[30vh] overflow-y-auto">
        {events.map((e) => {
          const left = remaining(e, now);
          const title = e.summary || '(no title)';
          return (
            <div
              key={e.id}
              role="button"
              tabIndex={0}
              onClick={() => onPick(e)}
              onKeyDown={(ev) => {
                if (ev.key === 'Enter' || ev.key === ' ') {
                  ev.preventDefault();
                  onPick(e);
                }
              }}
              title={
                e.countdown === 'series'
                  ? `${title} — next on ${when(e)}, repeats`
                  : `${title} — ${when(e)}`
              }
              className="group flex cursor-pointer items-center gap-2 rounded px-2 py-[6px] text-sm hover:bg-[var(--bg-hover)]"
            >
              <span
                aria-hidden
                className="block h-[14px] w-[3px] flex-shrink-0 rounded-full"
                style={{ background: retintEventBackground(e.background_color, theme) }}
              />
              <span className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap">
                {title}
              </span>
              {e.countdown === 'series' && (
                <span
                  aria-label="repeats"
                  className="flex-shrink-0 text-[0.72rem] leading-none text-[var(--text-faint)]"
                >
                  ↻
                </span>
              )}
              <span
                className={clsx(
                  'flex-shrink-0 rounded-full px-[7px] py-[1px] text-[0.72rem] tabular-nums',
                  left.near
                    ? 'bg-[var(--today-bg)] text-[var(--today-fg)]'
                    : 'bg-[var(--bg-muted)] text-[var(--text-muted)]'
                )}
              >
                {left.text}
              </span>
              <button
                type="button"
                onClick={(ev) => {
                  ev.stopPropagation();
                  onRemove(e.id);
                }}
                title="Stop counting down"
                aria-label={`stop counting down to ${title}`}
                className="flex-shrink-0 rounded p-[2px] leading-none text-[var(--text-muted)] opacity-0 hover:bg-[var(--bg-hover)] hover:text-[var(--text)] focus:opacity-100 group-hover:opacity-100"
              >
                <svg
                  viewBox="0 0 16 16"
                  width="12"
                  height="12"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  aria-hidden
                >
                  <path d="M4 4l8 8M12 4l-8 8" />
                </svg>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
